import {useState, useEffect} from 'react';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {toast} from '@common-web/hooks/use-toast';
import {useAuthStore} from '@common-web/stores/authStore';
import {UserPlus, Loader2} from 'lucide-react';
import {SYSTEM_ROLES} from '@common/roles';
import {CreateRegistrationResult, INVITE_EXPIRATION_DAY_OPTIONS} from '@internal/pages/users/new';
import {FeatureFlagEditor, getDefaultFeatureFlags} from './featureFlagConfig';
import {AssignableRolePicker} from './AssignableRolePicker';

export function ClientUserForm({onSuccess}: {onSuccess: (data: CreateRegistrationResult) => void}) {
  const isInternalUser = useAuthStore((state) => state.isInternalUser());

  const [clientForm, setClientForm] = useState({
    email: '',
    name: '',
    clientId: '',
    affiliation: '',
    role: SYSTEM_ROLES.client.admin.key as string,
    expiryDays: 7,
    featureFlags: getDefaultFeatureFlags(),
  });

  const {data: clients, isLoading: clientsIsLoading} =
    trpc.internal.userManagement.getClients.useQuery();

  // For client users, auto-select their client
  useEffect(() => {
    if (!isInternalUser && clients && clients.length === 1 && !clientForm.clientId) {
      setClientForm((prev) => ({...prev, clientId: clients[0].id}));
    }
  }, [clients, isInternalUser, clientForm.clientId]);

  const createClientUser = trpc.internal.userManagement.createClientUser.useMutation({
    onSuccess: (data) => {
      onSuccess(data.registration);
      toast({
        title: 'Registration created',
        description: `Access code has been generated for ${clientForm.email}`,
      });
    },
    onError: createMutationErrorHandler('Error creating registration'),
  });

  const handleClientSubmit = () => {
    const selectedClient = clients?.find((a) => a.id === clientForm.clientId);
    createClientUser.mutate({
      ...clientForm,
      clientId: selectedClient?.id || clientForm.affiliation,
    });
  };

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b'>
        <CardTitle className='text-lg font-semibold text-brand-navy'>
          Client User Registration
        </CardTitle>
      </CardHeader>
      <CardContent className='p-6 space-y-6'>
        <div className='grid grid-cols-2 gap-6'>
          <div className='space-y-2'>
            <Label htmlFor='client-email'>Email Address</Label>
            <Input
              id='client-email'
              type='email'
              placeholder='user@example.com'
              value={clientForm.email}
              onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='client-name'>Full Name</Label>
            <Input
              id='client-name'
              placeholder='John Doe'
              value={clientForm.name}
              onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
            />
          </div>
        </div>
        {isInternalUser ? (
          <div className='space-y-2'>
            <Label htmlFor='client-affiliation'>Client Affiliation</Label>
            {clientsIsLoading ? (
              <Skeleton className='h-10 w-full' />
            ) : (
              <Select
                value={clientForm.clientId}
                onValueChange={(value) => setClientForm({...clientForm, clientId: value})}
              >
                <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                  <SelectValue placeholder='Select a client' />
                </SelectTrigger>
                <SelectContent>
                  {clients?.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ) : (
          clients &&
          clients.length > 0 && (
            <div className='space-y-2'>
              <Label>Client</Label>
              <div className='px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-brand-navy font-medium'>
                {clients[0].name}
              </div>
            </div>
          )
        )}
        <AssignableRolePicker
          scope='CLIENT'
          clientId={clientForm.clientId || undefined}
          idPrefix='client'
          value={clientForm.role}
          onChange={(role) => setClientForm({...clientForm, role})}
        />
        <FeatureFlagEditor
          idPrefix='client'
          flags={clientForm.featureFlags}
          onChange={(featureFlags) => setClientForm({...clientForm, featureFlags})}
        />
        <div className='space-y-2'>
          <Label htmlFor='client-expiry'>Code Expiry (days)</Label>
          <Select
            value={clientForm.expiryDays.toString()}
            onValueChange={(value) => setClientForm({...clientForm, expiryDays: parseInt(value)})}
          >
            <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INVITE_EXPIRATION_DAY_OPTIONS.map((days) => (
                <SelectItem key={days} value={days.toString()}>
                  {days} day{days > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold w-full'
          onClick={handleClientSubmit}
          disabled={
            !clientForm.email ||
            !clientForm.name ||
            !clientForm.clientId ||
            !clientForm.role ||
            createClientUser.isPending
          }
        >
          {createClientUser.isPending ? (
            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
          ) : (
            <UserPlus className='h-4 w-4 mr-2' />
          )}
          Create Client User Registration
        </Button>
      </CardContent>
    </Card>
  );
}
